import { Stack, Text } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import { HiMiniExclamationCircle } from 'react-icons/hi2';
import useCreateAutomation from '@/hooks/api/automations/useCreateAutomation.ts';
import ModalConfirmationButtons from '@/components/StyledModal/ModalConfirmationButtons.tsx';
import StyledTextInput from '@/components/StyledInputFields/StyledTextInput.tsx';
import StyledTextarea from '@/components/StyledInputFields/StyledTextarea.tsx';
import { AUTOMATION_NAME_MAX_LENGTH } from '@/utils/constants.ts';

type Props = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
};

const CreateAutomationModal = ({ showModal, setShowModal }: Props) => {
  const { mutate: createAutomation, isPending, error } = useCreateAutomation();

  const [automationName, setAutomationName] = useState<string>('');
  const [automationDescription, setAutomationDescription] = useState<string>('');

  const [automationNameError, setAutomationNameError] = useState<string>('');

  useEffect(() => {
    if (error) {
      setAutomationNameError(error.message);
    }
  }, [error]);

  return (
    <StyledModal
      opened={showModal}
      onClose={() => setShowModal(false)}
      title={'New Automation'}
      size={'md'}
      // Custom layout using Mantine Stack for spacing
    >
      <Stack gap="md">
        <StyledTextInput
          label="Automation name"
          placeholder="Add a name"
          value={automationName}
          maxLength={AUTOMATION_NAME_MAX_LENGTH}
          onChange={(e) => {
            setAutomationName(e.currentTarget.value);
            if (e.currentTarget.value) {
              setAutomationNameError('');
            }
          }}
          error={
            automationNameError && (
              <Text c="red" size="xs">
                <HiMiniExclamationCircle color="red" size="1em" style={{ marginRight: '0.5em' }} />
                {automationNameError}
              </Text>
            )
          }
          required
        />
        <StyledTextarea
          label="Automation Description"
          placeholder='"You are an electronic health record patient history analyst. You work for HealthCo. a company that provides solutions to hospitals and clinics."'
          value={automationDescription}
          onChange={(e) => setAutomationDescription(e.currentTarget.value)}
          required={false}
          autosize={true}
          minRows={3}
        />
      </Stack>
      <ModalConfirmationButtons
        onClose={() => setShowModal(false)}
        onConfirm={() => {
          createAutomation({
            name: automationName,
            description: automationDescription,
          });
          setShowModal(false);
        }}
        confirmButtonProps={{
          loading: isPending,
          disabled: automationName.length === 0 || !!automationNameError,
        }}
        confirmText={'Create'}
      />
    </StyledModal>
  );
};

export default CreateAutomationModal;
